/**
 * @fileoverview Hook wrapper para otimização por plataforma.
 * Garante que hooks específicos sejam executados apenas nas plataformas apropriadas.
 * 
 * @module usePlatformOptimizedSync
 * @since 1.0.0
 */

import { useDeviceDetection } from '../utils/deviceDetection';
import useSessionRefresh from './useSessionRefresh';
import { useMobileSync } from './useMobileSync';
import { loadCartAsync } from '../store/slices/cartSlice';
import { loadFavoritesAsync } from '../store/slices/favoritesSlice';

/**
 * Hook otimizado que executa estratégias diferentes por plataforma:
 * - Desktop: useSessionRefresh (refresh automático via cookies/sessão)
 * - Mobile: useMobileSync (localStorage + sincronização manual)
 * 
 * @hook
 * @returns {Object} Informações sobre a estratégia de sincronização ativa
 */
export const usePlatformOptimizedSync = () => {
  const { isMobile, isDesktop } = useDeviceDetection();
  
  // Sempre chama todos os hooks para evitar violação das regras dos hooks
  const mobileSync = useMobileSync();
  
  // useSessionRefresh sempre é chamado, mas só executa lógica se não for mobile
  useSessionRefresh(loadCartAsync, loadFavoritesAsync, isMobile);
  
  // Retorna informações baseadas na plataforma
  if (isDesktop) {
    return {
      strategy: 'desktop-session-refresh',
      platform: 'desktop',
      description: 'Refresh automático via cookies/sessão a cada 60s'
    };
  }
  
  if (isMobile) {
    return {
      strategy: 'mobile-localstorage-sync',
      platform: 'mobile',
      description: 'localStorage + sincronização manual/automática',
      ...mobileSync
    };
  }
  
  // Fallback
  return {
    strategy: 'unknown',
    platform: 'unknown',
    description: 'Plataforma não detectada'
  };
};

export default usePlatformOptimizedSync;